import { Link } from "react-router-dom";
import { Card, PhasePill, StarButton } from "./ui";
import { flag } from "../lib/flag";
import type { ElectionSummary } from "@shared/types";
import { candidatePath } from "@shared/share-links";
import { useI18n } from "../i18n";

// Stable-ish color per candidate name (kept distinct from the ember accent).
const COLORS = ["#5b9dff", "#a855f7", "#34e3cf", "#ec4899", "#f4c245", "#36d07a"];
function colorFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
}

/** Race card shared by the Country page, the Home hub, and Favorites. */
export function ElectionCard({
  e,
  fav,
  onFav,
  onOpen,
  onOpenRace,
  showCountry = false,
}: {
  e: ElectionSummary;
  fav: boolean;
  onFav: () => void;
  onOpen: () => void;
  /** Open the candidacy hub for this race (any phase). */
  onOpenRace?: () => void;
  showCountry?: boolean;
}) {
  const { t, locale } = useI18n();
  const title = showCountry ? (
    <span className="flex items-center gap-2 min-w-0">
      <span>{flag(e.countryCode)}</span>
      <span className="office text-base truncate">{e.countryName}</span>
      <span className="text-muted text-sm capitalize">· {e.type === "president" ? t("office.president") : t("office.congress")}</span>
    </span>
  ) : (
    <span className="office capitalize">{e.type === "president" ? t("office.president") : t("office.congress")}</span>
  );

  if (e.scheduled) {
    return (
      <Card className={onOpenRace ? "card-hov" : ""}>
        <div onClick={onOpenRace}>
          <div className="race-top flex justify-between items-start">
            {title}
            <PhasePill phase="soon" startsAt={e.votesStartAt} />
          </div>
          <div className="text-sm text-muted mt-3">{t("election.notOpen")}</div>
          <div className="eyebrow mt-3">{t("election.opens", { date: new Date(e.votesStartAt).toLocaleDateString(locale) })}</div>
          {onOpenRace && <div className="text-xs text-accent mt-3 font-mono">{t("forecast.viewCandidates")} →</div>}
        </div>
      </Card>
    );
  }

  const winnerName = e.winners[0]?.username;
  const fpct = e.favorite?.winPct ?? 0;

  return (
    <Card className="card-hov">
      <div onClick={onOpen}>
        <div className="race-top flex justify-between items-start gap-2">
          {title}
          <span className="flex items-center gap-2 shrink-0">
            <PhasePill phase={e.phase} startsAt={e.votesStartAt} />
            <StarButton active={fav} onClick={onFav} />
          </span>
        </div>

        {e.phase === "done" ? (
          <div className="lede">
            <span className="av" style={{ background: winnerName ? colorFor(winnerName) : "#2a3142" }}>
              {winnerName ? winnerName[0]?.toUpperCase() : "—"}
            </span>
            <span className="min-w-0">
              <span className="nm truncate block">
                {winnerName ? (
                  <>
                    🏆{" "}
                    <Link to={candidatePath(winnerName)} onClick={(ev) => ev.stopPropagation()} className="hover:underline">
                      {winnerName}
                    </Link>
                  </>
                ) : (
                  t("election.closed")
                )}
              </span>
              <span className="pt">{e.winners.length > 1 ? t("election.nElected", { n: e.winners.length }) : t("election.finalResult")}</span>
            </span>
          </div>
        ) : e.favorite ? (
          <>
            <div className="bar">
              <div className="seg" style={{ width: `${Math.max(12, fpct)}%`, background: colorFor(e.favorite.username) }}>
                {fpct}%
              </div>
            </div>
            <div className="lede">
              <span className="av" style={{ background: colorFor(e.favorite.username) }}>{e.favorite.username[0]?.toUpperCase()}</span>
              <span className="min-w-0">
                <span className="nm truncate block">
                  <Link to={candidatePath(e.favorite.username)} onClick={(ev) => ev.stopPropagation()} className="hover:underline">
                    {e.favorite.username}
                  </Link>
                </span>
                <span className="pt">{t("election.projectedFavorite")}</span>
              </span>
              <span className="pct">
                <span className="big">{fpct}%</span>
                <span className="lab block">{t("election.winProb")}</span>
              </span>
            </div>
          </>
        ) : (
          <div className="text-sm text-muted mt-3">{t("election.noCandidates")}</div>
        )}

        <div className="flex justify-between eyebrow mt-3 normal-case">
          <span>{phaseHint(e, t, locale)}</span>
          {e.phase === "live" && e.liveLeader && <span>{t("election.liveLeader", { name: e.liveLeader.username, votes: e.liveLeader.voteCount })}</span>}
        </div>

        {onOpenRace && (
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              onOpenRace();
            }}
            className="text-xs text-accent mt-3 font-mono hover:underline"
          >
            {t("forecast.viewCandidates")} →
          </button>
        )}
      </div>
    </Card>
  );
}

function phaseHint(e: ElectionSummary, t: ReturnType<typeof useI18n>["t"], locale: string): string {
  const d = (iso: string) => new Date(iso).toLocaleDateString(locale);
  if (e.phase === "soon") return t("election.opens", { date: d(e.votesStartAt) });
  if (e.phase === "live") return t("election.closes", { date: d(e.votesEndAt) });
  return t("election.closedOn", { date: d(e.votesEndAt) });
}
